import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, MapPin, Target, Clock, Tag, Save, Edit2, UserPlus, UserCheck, MessageSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Profile as ProfileType, AREAS_OF_INTEREST, FREQUENCIES } from '@/lib/types';

const springGentle = { type: 'spring', stiffness: 300, damping: 35 };

export default function Profile() {
  const { userId } = useParams();
  const { user, profile: myProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const isOwnProfile = !userId || userId === user?.id;
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [friendshipStatus, setFriendshipStatus] = useState<string | null>(null);
  const [friendshipId, setFriendshipId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [form, setForm] = useState({
    full_name: '', bio: '', location: '', accountability_goal: '',
    areas_of_interest: [] as string[], preferred_frequency: '',
  });

  useEffect(() => {
    if (isOwnProfile && myProfile) {
      setProfile(myProfile);
      setForm({
        full_name: myProfile.full_name || '',
        bio: myProfile.bio || '',
        location: myProfile.location || '',
        accountability_goal: myProfile.accountability_goal || '',
        areas_of_interest: myProfile.areas_of_interest || [],
        preferred_frequency: myProfile.preferred_frequency || '',
      });
      setLoading(false);
    } else if (userId) {
      fetchProfile(userId);
      fetchFriendship(userId);
    }
  }, [userId, myProfile, isOwnProfile]);

  async function fetchProfile(id: string) {
    setLoading(true);
    const { data } = await supabase.from('profiles').select('*').eq('id', id).single();
    setProfile(data as ProfileType);
    setLoading(false);
  }

  async function fetchFriendship(otherId: string) {
    if (!user) return;
    const { data } = await supabase
      .from('friendships')
      .select('*')
      .or(`and(requester_id.eq.${user.id},addressee_id.eq.${otherId}),and(requester_id.eq.${otherId},addressee_id.eq.${user.id})`)
      .single();
    if (data) {
      setFriendshipStatus(data.status);
      setFriendshipId(data.id);
    }
  }

  async function handleSave() {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from('profiles').update({
      full_name: form.full_name,
      bio: form.bio,
      location: form.location,
      accountability_goal: form.accountability_goal,
      areas_of_interest: form.areas_of_interest,
      preferred_frequency: form.preferred_frequency || null,
    }).eq('id', user.id);
    setSaving(false);
    if (error) { toast.error('Failed to save profile'); return; }
    toast.success('Profile updated!');
    setEditing(false);
    await refreshProfile();
  }

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const ext = file.name.split('.').pop();
    const path = `${user.id}/avatar.${ext}`;
    const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    if (upErr) { toast.error('Upload failed'); return; }
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    await supabase.from('profiles').update({ avatar_url: data.publicUrl + '?t=' + Date.now() }).eq('id', user.id);
    await refreshProfile();
    toast.success('Avatar updated!');
  }

  async function sendFriendRequest() {
    if (!user || !userId) return;
    const { error } = await supabase.from('friendships').insert({ requester_id: user.id, addressee_id: userId });
    if (error) { toast.error('Could not send request'); return; }
    toast.success('Friend request sent!');
    setFriendshipStatus('pending');
  }

  async function acceptRequest() {
    if (!friendshipId) return;
    await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId);
    setFriendshipStatus('accepted');
    toast.success('Partner request accepted!');
  }

  function toggleArea(area: string) {
    setForm(f => ({
      ...f,
      areas_of_interest: f.areas_of_interest.includes(area)
        ? f.areas_of_interest.filter(a => a !== area)
        : [...f.areas_of_interest, area],
    }));
  }

  if (loading) return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    </Layout>
  );

  if (!profile) return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-muted-foreground">Profile not found.</p>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* @section: profile-header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={springGentle}>
          <div className="bg-card border border-border rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 4px 24px color-mix(in srgb, var(--primary) 8%, transparent)' }}>
            {/* Banner */}
            <div className="h-32 relative"
              style={{ background: 'linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 70%, var(--accent)) 100%)' }}>
              {/* Avatar */}
              <div className="absolute -bottom-10 left-8">
                <div className="relative">
                  <Avatar className="w-20 h-20 border-4 border-background">
                    <AvatarImage src={isOwnProfile ? myProfile?.avatar_url ?? undefined : profile.avatar_url ?? undefined} />
                    <AvatarFallback className="text-2xl font-bold bg-primary/20 text-primary">
                      {(profile.full_name || profile.username)?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {isOwnProfile && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 w-7 h-7 bg-primary rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
                    >
                      <Camera className="w-3.5 h-3.5 text-primary-foreground" />
                    </button>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="pt-14 pb-6 px-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  {editing ? (
                    <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                      placeholder="Full name" className="text-xl font-bold h-10 w-64 rounded-xl mb-1" />
                  ) : (
                    <h1 className="text-2xl font-bold text-foreground">{profile.full_name || profile.username}</h1>
                  )}
                  <p className="text-muted-foreground text-sm">@{profile.username}</p>
                  {profile.location && !editing && (
                    <p className="text-muted-foreground text-sm flex items-center gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {profile.location}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isOwnProfile ? (
                    editing ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="rounded-xl">
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-xl"
                          style={{ boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 30%, transparent)' }}>
                          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-xl">
                        <Edit2 className="w-4 h-4 mr-1" /> Edit Profile
                      </Button>
                    )
                  ) : (
                    <div className="flex gap-2">
                      {!friendshipStatus && (
                        <Button size="sm" onClick={sendFriendRequest} className="rounded-xl"
                          style={{ boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 30%, transparent)' }}>
                          <UserPlus className="w-4 h-4 mr-1" /> Add Partner
                        </Button>
                      )}
                      {friendshipStatus === 'pending' && (
                        <Badge variant="secondary" className="px-3 py-1.5 rounded-xl">
                          <Clock className="w-3.5 h-3.5 mr-1" /> Request Sent
                        </Badge>
                      )}
                      {friendshipStatus === 'accepted' && (
                        <Button size="sm" variant="outline" onClick={() => navigate(`/chat/${userId}`)} className="rounded-xl">
                          <MessageSquare className="w-4 h-4 mr-1" /> Message
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <Separator className="my-6" />

              {/* Bio */}
              <div className="space-y-5">
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">About</Label>
                  {editing ? (
                    <Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                      placeholder="Tell potential partners about yourself…" className="mt-2 rounded-xl resize-none" rows={3} />
                  ) : (
                    <p className="mt-1.5 text-foreground text-sm leading-relaxed">{profile.bio || <span className="text-muted-foreground italic">No bio yet</span>}</p>
                  )}
                </div>

                {/* Location (edit) */}
                {editing && (
                  <div>
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Location</Label>
                    <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                      placeholder="City, Country" className="mt-2 rounded-xl h-10" />
                  </div>
                )}

                {/* Goal */}
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5" /> Accountability Goal
                  </Label>
                  {editing ? (
                    <Input value={form.accountability_goal} onChange={e => setForm(f => ({ ...f, accountability_goal: e.target.value }))}
                      placeholder="e.g. Launch my side project in 3 months" className="mt-2 rounded-xl h-10" />
                  ) : (
                    <p className="mt-1.5 text-foreground text-sm">{profile.accountability_goal || <span className="text-muted-foreground italic">Not set</span>}</p>
                  )}
                </div>

                {/* Frequency */}
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Preferred Check-In Frequency
                  </Label>
                  {editing ? (
                    <Select value={form.preferred_frequency} onValueChange={v => setForm(f => ({ ...f, preferred_frequency: v }))}>
                      <SelectTrigger className="mt-2 rounded-xl h-10"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    profile.preferred_frequency ? (
                      <Badge variant="secondary" className="mt-1.5 capitalize rounded-lg">
                        {profile.preferred_frequency}
                      </Badge>
                    ) : <p className="mt-1.5 text-muted-foreground text-sm italic">Not set</p>
                  )}
                </div>

                {/* Areas of Interest */}
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" /> Areas of Interest
                  </Label>
                  {editing ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {AREAS_OF_INTEREST.map(area => (
                        <button key={area} type="button" onClick={() => toggleArea(area)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                            form.areas_of_interest.includes(area)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                          }`}>
                          {area}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {profile.areas_of_interest?.length > 0
                        ? profile.areas_of_interest.map(a => (
                          <Badge key={a} variant="secondary" className="rounded-lg text-xs">{a}</Badge>
                        ))
                        : <p className="text-muted-foreground text-sm italic">None selected</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
